import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Index from './pages/Index';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';

import { Toaster } from '@/components/ui/toaster';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Services from './pages/Services';
import AISolutions from './pages/AISolutions';
import Portfolio from './pages/Portfolio';
import { ChatbotProvider } from './hooks/useChatbot';
import ChatbotUI from './components/chatbot/ChatbotUI';
import MaxPage from './pages/MaxPage';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import SupportHub from './pages/SupportHub';
import ScrollToTop from './components/utils/ScrollToTop';
import SecurePlatformConnection from './components/auth/SecurePlatformConnection';
import { AuthProvider } from './hooks/useAuth';
import { AdminAuthProvider } from './hooks/useAdminAuth';
import DemoDebtCollector from './pages/DemoDebtCollector';
import BulgarianDemoDebtCollector from './pages/BulgarianDemoDebtCollector';
import InternalOutboundCalling from './pages/InternalOutboundCalling';
import Authentication from './pages/Authentication';
import LearnLanding from './pages/learn/LearnLanding';
import LearnAbout from './pages/learn/LearnAbout';
import LearnCatalog from './pages/learn/LearnCatalog';
import LearnPricing from './pages/learn/LearnPricing';
import LearnDashboard from './pages/learn/app/LearnDashboard';
import LearnModules from './pages/learn/app/LearnModules';
import LearnModulePlayer from './pages/learn/app/LearnModulePlayer';
import LearnAdminDashboard from "@/pages/learn/admin/LearnAdminDashboard";
import LearnAdminCoupons from "@/pages/learn/admin/LearnAdminCoupons";
import LearnAdminCertificates from "@/pages/learn/admin/LearnAdminCertificates";
import LearnAdminContentEditor from "@/pages/learn/admin/LearnAdminContentEditor";
import LearnAuth from './pages/learn/LearnAuth';
import LearnAdminAuth from './pages/learn/admin/LearnAdminAuth';
import ProtectedRoute from './components/learn/ProtectedRoute';
import CapstoneROI from './pages/learn/app/CapstoneROI';
import CapstonePrivacy from './pages/learn/app/CapstonePrivacy';
// Admin routes
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTenants from './pages/admin/AdminTenants';
import AdminTenantDetail from './pages/admin/AdminTenantDetail';
import AdminBotDetail from './pages/admin/AdminBotDetail';
import AdminTemplates from './pages/admin/AdminTemplates';
import AdminGovernance from './pages/admin/AdminGovernance';
import AdminReports from './pages/admin/AdminReports';
import AdminInbox from './pages/admin/AdminInbox';
import AdminQASuites from './pages/admin/AdminQASuites';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
// Advisory Assistants public pages
import AssistantsHub from './pages/assistants/AssistantsHub';
import AssistantsIndustries from './pages/assistants/AssistantsIndustries';
import AssistantsAccounting from './pages/assistants/AssistantsAccounting';
import AssistantsGovernance from './pages/assistants/AssistantsGovernance';
import AssistantsGovernanceAccounting from './pages/assistants/AssistantsGovernanceAccounting';
import AssistantsGetAssistant from './pages/assistants/AssistantsGetAssistant';
import AssistantsPricing from './pages/assistants/AssistantsPricing';
import AssistantsCaseExamples from './pages/assistants/AssistantsCaseExamples';
import AssistantsCheckoutSuccess from './pages/assistants/AssistantsCheckoutSuccess';
import ClientOnboarding from './pages/assistants/ClientOnboarding';
import ClientBotPreview from './pages/ClientBotPreview';
import Glossary from './pages/Glossary';
import WorkflowAutomationMarlborough from './pages/WorkflowAutomationMarlborough';
import AIAutomationProfessionalServices from './pages/AIAutomationProfessionalServices';
import AIWorkflowAutomationNZ from './pages/AIWorkflowAutomationNZ';
import ArchitectWorkflowIntelligence from './pages/case-studies/ArchitectWorkflowIntelligence';
import CancerCareCampaign from './pages/campaign/CancerCareCampaign';
import CampaignPage from './pages/campaign/CampaignPage';
import AdminCampaigns from './pages/admin/AdminCampaigns';
import AdminCampaignDetail from './pages/admin/AdminCampaignDetail';

function App() {
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <HelmetProvider>
      <ChatbotProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/services" element={<Services />} />
                <Route path="/ai-solutions" element={<AISolutions />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/max" element={<Navigate to="/" replace />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/help" element={<SupportHub />} />
                <Route path="/login" element={<Navigate to="https://app.thinktomorrow.ai/" replace />} />
                <Route path="/connect" element={<SecurePlatformConnection />} />
                <Route path="/onboarding" element={<SecurePlatformConnection />} />
                <Route path="/demo" element={<DemoDebtCollector />} />
                <Route path="/demo-bg" element={<BulgarianDemoDebtCollector />} />
                <Route path="/internal-outbound" element={<InternalOutboundCalling />} />
                <Route path="/authentication" element={<Authentication />} />
                <Route path="/glossary" element={<Glossary />} />
                <Route path="/workflow-automation-marlborough" element={<WorkflowAutomationMarlborough />} />
                <Route path="/ai-automation-professional-services" element={<AIAutomationProfessionalServices />} />
                <Route path="/ai-workflow-automation-nz" element={<AIWorkflowAutomationNZ />} />
                <Route path="/case-studies/architect-workflow-intelligence" element={<ArchitectWorkflowIntelligence />} />
                
                {/* Campaign Bot Routes */}
                <Route path="/campaign/cancer-care" element={<CancerCareCampaign />} />
                <Route path="/campaign/:slug" element={<CampaignPage />} />
                
                {/* Advisory Assistants Public Routes */}
                <Route path="/assistants" element={<AssistantsHub />} />
                <Route path="/assistants/industries" element={<AssistantsIndustries />} />
                <Route path="/assistants/industries/accounting" element={<AssistantsAccounting />} />
                <Route path="/assistants/governance" element={<AssistantsGovernance />} />
                <Route path="/assistants/governance/accounting" element={<AssistantsGovernanceAccounting />} />
                <Route path="/assistants/get-assistant" element={<AssistantsGetAssistant />} />
                <Route path="/assistants/pricing" element={<AssistantsPricing />} />
                <Route path="/assistants/case-examples" element={<AssistantsCaseExamples />} />
                <Route path="/assistants/checkout-success" element={<AssistantsCheckoutSuccess />} />
                <Route path="/onboarding/:tenantId" element={<ClientOnboarding />} />
                <Route path="/preview/:botId" element={<ClientBotPreview />} />
                
                {/* Admin Console Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
                <Route path="/admin/tenants" element={<AdminProtectedRoute><AdminTenants /></AdminProtectedRoute>} />
                <Route path="/admin/tenants/:tenantId" element={<AdminProtectedRoute><AdminTenantDetail /></AdminProtectedRoute>} />
                <Route path="/admin/bots/:botId" element={<AdminProtectedRoute><AdminBotDetail /></AdminProtectedRoute>} />
                <Route path="/admin/qa-suites" element={<AdminProtectedRoute><AdminQASuites /></AdminProtectedRoute>} />
                <Route path="/admin/templates" element={<AdminProtectedRoute><AdminTemplates /></AdminProtectedRoute>} />
                <Route path="/admin/governance" element={<AdminProtectedRoute><AdminGovernance /></AdminProtectedRoute>} />
                <Route path="/admin/reports" element={<AdminProtectedRoute><AdminReports /></AdminProtectedRoute>} />
                <Route path="/admin/inbox" element={<AdminProtectedRoute><AdminInbox /></AdminProtectedRoute>} />
                <Route path="/admin/campaigns" element={<AdminProtectedRoute><AdminCampaigns /></AdminProtectedRoute>} />
                <Route path="/admin/campaigns/:id" element={<AdminProtectedRoute><AdminCampaignDetail /></AdminProtectedRoute>} />
                
                {/* Learn LMS Routes */}
                <Route path="/learn" element={<LearnLanding />} />
                <Route path="/learn/about" element={<LearnAbout />} />
                <Route path="/learn/catalog" element={<LearnCatalog />} />
                <Route path="/learn/pricing" element={<LearnPricing />} />
                <Route path="/learn/auth" element={<LearnAuth />} />
                <Route path="/learn/admin/login" element={<LearnAdminAuth />} />
                <Route path="/learn/app/dashboard" element={<ProtectedRoute><LearnDashboard /></ProtectedRoute>} />
                <Route path="/learn/app/modules" element={<ProtectedRoute><LearnModules /></ProtectedRoute>} />
                <Route path="/learn/app/modules/:slug" element={<ProtectedRoute><LearnModulePlayer /></ProtectedRoute>} />
                <Route path="/learn/app/capstone/roi" element={<ProtectedRoute><CapstoneROI /></ProtectedRoute>} />
                <Route path="/learn/app/capstone/privacy" element={<ProtectedRoute><CapstonePrivacy /></ProtectedRoute>} />
                <Route path="/learn/admin/dashboard" element={<ProtectedRoute requireAdmin><LearnAdminDashboard /></ProtectedRoute>} />
                <Route path="/learn/admin/coupons" element={<ProtectedRoute requireAdmin><LearnAdminCoupons /></ProtectedRoute>} />
                <Route path="/learn/admin/certificates" element={<ProtectedRoute requireAdmin><LearnAdminCertificates /></ProtectedRoute>} />
                <Route path="/learn/admin/content-editor" element={<ProtectedRoute requireAdmin><LearnAdminContentEditor /></ProtectedRoute>} />
                
                {/* Redirect old routes to home */}
                <Route path="/courses" element={<Navigate to="/" replace />} />
                <Route path="/ai-library" element={<Navigate to="/" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <ChatbotUI />
            </BrowserRouter>
            <Toaster />
            
          </AdminAuthProvider>
        </AuthProvider>
      </ChatbotProvider>
    </HelmetProvider>
  );
}

export default App;
